import { ReactNode, useEffect, useRef } from 'react';
import { PullToRefresh } from '../components/ui/pull-to-refresh';
import { AssistantMenu } from '../features/assistant/assistant-menu';
import { nudgeIosPwaTopChrome } from '../lib/ios-pwa-chrome';
import { AppMobileTabBar } from './app-mobile-tab-bar';
import { AppSidebar } from './app-sidebar';
import { AppTopbar } from './app-topbar';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const mainRef = useRef<HTMLElement | null>(null);

  // iOS 26+ Liquid Glass amostra o body no overscroll; alinhar à topbar evita faixa “errada”.
  useEffect(() => {
    const previous = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'var(--surface)';
    return () => {
      document.body.style.backgroundColor = previous;
    };
  }, []);

  // iOS 27: blur preso no topo do PWA some após um scroll (workaround de cold start).
  useEffect(() => {
    const run = () => nudgeIosPwaTopChrome(mainRef.current);
    run();
    const t0 = window.setTimeout(run, 50);
    const t1 = window.setTimeout(run, 300);

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) run();
    };
    const onOrientation = () => {
      window.setTimeout(run, 250);
    };

    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('orientationchange', onOrientation);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, []);

  return (
    <div className="flex min-h-dvh bg-surface">
      <AppSidebar />
      <div className="flex min-h-dvh min-w-0 flex-1 flex-col bg-surface">
        <AppTopbar />
        <div className="relative flex min-h-0 flex-1 flex-col">
          <PullToRefresh scrollRef={mainRef} />
          <main
            ref={mainRef}
            className="min-h-0 flex-1 overflow-auto pb-[calc(4.75rem+env(safe-area-inset-bottom))] md:pb-0"
          >
            {children}
          </main>
        </div>
        <AppMobileTabBar />
        <AssistantMenu />
      </div>
    </div>
  );
}
